/**
 * Mini TV run store
 *
 * Holds one run per schedule (schedule_id). Each schedule has its own mini TV:
 * when viewing a schedule, only that schedule's run is shown. Runs for other
 * schedules stay in the store so switching back shows the correct execution/ended state.
 */

import React from 'react';

const ACTIONS = {
  START_RUN: 'START_RUN',
  SET_RUN_ENDED: 'SET_RUN_ENDED',
  CLEAR_RUN: 'CLEAR_RUN',
} as const;

export interface MiniTvRunPhase {
  phase: 'execution' | 'ended';
  [key: string]: unknown;
}

interface MiniTvRunState {
  runsByScheduleId: Record<string, MiniTvRunPhase>;
}

type ScheduleId = string | number;

type StartRunAction = { type: typeof ACTIONS.START_RUN; scheduleId: ScheduleId; payload: Record<string, unknown> };
type SetRunEndedAction = { type: typeof ACTIONS.SET_RUN_ENDED; scheduleId: ScheduleId };
type ClearRunAction = { type: typeof ACTIONS.CLEAR_RUN; scheduleId: ScheduleId };
type MiniTvRunAction = StartRunAction | SetRunEndedAction | ClearRunAction;

const initialState: MiniTvRunState = { runsByScheduleId: {} };

function miniTvRunReducer(state: MiniTvRunState, action: MiniTvRunAction): MiniTvRunState {
  switch (action.type) {
    case ACTIONS.START_RUN: {
      const { scheduleId, payload } = action;
      if (scheduleId == null) return state;
      return {
        runsByScheduleId: {
          ...state.runsByScheduleId,
          [String(scheduleId)]: { ...payload, phase: 'execution' as const },
        },
      };
    }
    case ACTIONS.SET_RUN_ENDED: {
      const { scheduleId } = action;
      if (scheduleId == null) return state;
      const run = state.runsByScheduleId[String(scheduleId)];
      if (!run) return state;
      return {
        runsByScheduleId: {
          ...state.runsByScheduleId,
          [String(scheduleId)]: { ...run, phase: 'ended' as const },
        },
      };
    }
    case ACTIONS.CLEAR_RUN: {
      const { scheduleId } = action;
      if (scheduleId == null) return state;
      const next = { ...state.runsByScheduleId };
      delete next[String(scheduleId)];
      return { runsByScheduleId: next };
    }
    default:
      return state;
  }
}

export interface MiniTvRunContextValue {
  getRun: (scheduleId: ScheduleId) => MiniTvRunPhase | null;
  getActiveExecutionRuns: () => Array<{ scheduleId: string; run: MiniTvRunPhase }>;
  startRun: (scheduleId: ScheduleId, payload: Record<string, unknown>) => void;
  setRunEnded: (scheduleId: ScheduleId) => void;
  clearRun: (scheduleId: ScheduleId) => void;
}

export const MiniTvRunContext = React.createContext<MiniTvRunContextValue | null>(null);

export function useMiniTvRun(): MiniTvRunContextValue {
  const ctx = React.useContext(MiniTvRunContext);
  if (!ctx) {
    throw new Error('useMiniTvRun must be used within MiniTvRunProvider');
  }
  return ctx;
}

interface MiniTvRunProviderProps {
  children: React.ReactNode;
}

export function MiniTvRunProvider({ children }: MiniTvRunProviderProps): React.ReactElement {
  const [state, dispatch] = React.useReducer(miniTvRunReducer, initialState);

  const getRun = React.useCallback(
    (scheduleId: ScheduleId) => {
      if (scheduleId == null) return null;
      return state.runsByScheduleId[String(scheduleId)] ?? null;
    },
    [state.runsByScheduleId]
  );

  const getActiveExecutionRuns = React.useCallback(() => {
    return Object.entries(state.runsByScheduleId)
      .filter(([, run]) => run?.phase === 'execution')
      .map(([scheduleId, run]) => ({ scheduleId, run: run! }))
      .sort((a, b) => String(a.scheduleId).localeCompare(String(b.scheduleId)));
  }, [state.runsByScheduleId]);

  const startRun = React.useCallback((scheduleId: ScheduleId, payload: Record<string, unknown>) => {
    dispatch({ type: ACTIONS.START_RUN, scheduleId, payload });
  }, []);

  const setRunEnded = React.useCallback((scheduleId: ScheduleId) => {
    dispatch({ type: ACTIONS.SET_RUN_ENDED, scheduleId });
  }, []);

  const clearRun = React.useCallback((scheduleId: ScheduleId) => {
    dispatch({ type: ACTIONS.CLEAR_RUN, scheduleId });
  }, []);

  const value = React.useMemo(
    () => ({
      getRun,
      getActiveExecutionRuns,
      startRun,
      setRunEnded,
      clearRun,
    }),
    [getRun, getActiveExecutionRuns, startRun, setRunEnded, clearRun]
  );

  return (
    <MiniTvRunContext.Provider value={value}>
      {children}
    </MiniTvRunContext.Provider>
  );
}

export default MiniTvRunProvider;
